/* eslint-disable jsx-a11y/click-events-have-key-events */
import {
  useTrackResourceActivities,
  useTrackVideoCurrentTime,
} from "@core/activities/hooks/resources/use-track-resource-activities";
import { useTrackResourceView } from "@core/activities/hooks/resources/use-track-resource-view";
import { useVideoSegments } from "@core/activities/hooks/resources/use-video-segments.hook";
import { Resource } from "@core/shared/types";
import {
  VideoPlayer2,
  type VideoPlayerManager2,
} from "@design-system/components/VideoPlayer2";
import { useEffect, useRef, useState } from "react";
import {
  useFullscreenDetection,
  useVideoHeaders,
  useVideoWatermark,
} from "../hooks";
import { shouldSendHlsHeaders } from "@app/resources/utils/hls-request-headers";

type ResourceVideoProps = {
  resource: Resource;
};

export function useVideoActivities(
  resource: Resource,
  playerRef?: React.RefObject<any>,
) {
  const { trackResourceView } = useTrackResourceView(resource);
  const { trackVideoActivity } = useTrackResourceActivities(resource);
  const { trackVideoCurrentTime } = useTrackVideoCurrentTime(resource);
  const {
    onTimeUpdate: onSegmentTimeUpdate,
    onSeeking: onSegmentSeeking,
    onSeeked: onSegmentSeeked,
    onPlay: onSegmentPlay,
    onPause: onSegmentPause,
    onStart: onSegmentStart,
    onEnd: onSegmentEnd,
    getTotalWatchedTime,
    getCompletionPercentage,
  } = useVideoSegments(resource, playerRef);

  // Track user interaction state to distinguish user-initiated vs system-initiated events
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const [lastUserInteraction, setLastUserInteraction] = useState<number>(0);

  const onPlay = async (player: VideoPlayerManager2) => {
    // console.log("onPlay", player);
    trackVideoActivity("play", player.currentTime);
    onSegmentPlay(player.currentTime);
  };

  const onPause = async (player: VideoPlayerManager2) => {
    const now = Date.now();
    const timeSinceLastInteraction = now - lastUserInteraction;
    const isPageVisible = !document.hidden;

    // Only track pause if it's likely user-initiated
    // User interactions include: clicks, keyboard events, touch events
    // System pauses happen when tab loses focus, page visibility changes, etc.
    const isLikelyUserInitiated =
      isUserInteracting || (timeSinceLastInteraction < 2000 && isPageVisible); // 2 seconds grace period + page must be visible

    // console.log("onPause", player, {
    //   isUserInteracting,
    //   timeSinceLastInteraction: `${timeSinceLastInteraction}ms`,
    //   isPageVisible,
    //   isLikelyUserInitiated
    // });

    trackVideoCurrentTime(player.currentTime, { force: true });

    if (isLikelyUserInitiated) {
      trackVideoActivity("pause", player.currentTime);
    } else {
      // console.log("Skipping pause tracking - likely system-initiated (page hidden or no recent interaction)");
    }

    onSegmentPause(player.currentTime);
  };

  const onTimeUpdate = (
    player: VideoPlayerManager2,
    currentTime: number,
    duration: number,
  ) => {
    // console.log("onTimeUpdate", currentTime, duration, player.isPlaying);
    if (!player.isPlaying) return;
    // console.log("onTimeUpdate", currentTime, duration);
    onSegmentTimeUpdate(currentTime);
    trackVideoCurrentTime(currentTime);
  };

  const onStart = async (player: VideoPlayerManager2, currentTime?: number) => {
    const time = currentTime ?? player.currentTime ?? 0;
    // console.log("onStart called with player:", player, "at time:", time);
    trackResourceView();
    onSegmentStart(time);
  };

  const onSeeking = (
    player: VideoPlayerManager2,
    fromTime: number,
    toTime: number,
  ) => {
    // console.log("onSeeking", player, fromTime, toTime);
    onSegmentSeeking(fromTime, toTime);
  };

  const onSeeked = (player: VideoPlayerManager2, toTime: number) => {
    // console.log("onSeeked", player, toTime);
    onSegmentSeeked(toTime);
    trackVideoCurrentTime(toTime, { force: true });
  };

  const onEnded = (player: VideoPlayerManager2, duration: number) => {
    // console.log("onEnded", player, duration);
    onSegmentEnd();

    // Log final completion stats
    const totalWatched = getTotalWatchedTime();
    const completionPercentage = getCompletionPercentage(duration);
    // console.log(`Final stats - Total watched: ${totalWatched}s, Completion: ${completionPercentage.toFixed(1)}%`);
    trackVideoCurrentTime(duration, { force: true });
  };

  // Track user interactions to distinguish user-initiated vs system-initiated events
  useEffect(() => {
    const handleUserInteraction = () => {
      setIsUserInteracting(true);
      setLastUserInteraction(Date.now());

      // Reset interaction state after 3 seconds of no interaction
      setTimeout(() => {
        setIsUserInteracting(false);
      }, 3000);
    };

    // Add event listeners for user interactions on video player specifically
    // This helps distinguish between video player interactions and other page interactions
    const videoPlayerElement =
      document.querySelector("[data-video-player]") || document;

    const events = ["click", "keydown", "touchstart", "mousedown"];

    events.forEach((event) => {
      videoPlayerElement.addEventListener(event, handleUserInteraction, {
        passive: true,
      });
    });

    // Also track page visibility changes to detect system-initiated pauses
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // console.log("Page became hidden - system pause likely");
        // Don't track pauses when page becomes hidden
      } else {
        // console.log("Page became visible");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      events.forEach((event) => {
        videoPlayerElement.removeEventListener(event, handleUserInteraction);
      });
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return {
    onPlay,
    onPause,
    onTimeUpdate,
    onStart,
    onSeeking,
    onSeeked,
    onEnded,
  };
}

export function ResourceVideo({ resource }: ResourceVideoProps) {
  
  if (!resource) return null;

  const playerRef = useRef<VideoPlayerManager2>(null);
  const [videoUrl, setVideoUrl] = useState(resource.url!);
  console.log("===================================", resource);

  const {
    onPlay,
    onPause,
    onTimeUpdate,
    onStart,
    onSeeking,
    onSeeked,
    onEnded,
  } = useVideoActivities(resource, playerRef);

  const { isFullScreen } = useFullscreenDetection();
  const { videoHeaders } = useVideoHeaders();
  useVideoWatermark({
    playerRef,
    isFullScreen,
    resourceUrl: resource.url!,
  });

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
      }}
      className="relative max-w-full"
    >
      {resource.url && (
        <VideoPlayer2
          ref={playerRef}
          key={resource.url}
          id={resource.id.toString()}
          url={videoUrl}
          className="min-h-[300px] md:min-h-[650px]"
          hlsHeaders={videoHeaders}
          validateHlsHeaders={shouldSendHlsHeaders}
          saveCurrentTime
          usesHLS
          onStart={onStart}
          onPlay={onPlay}
          onPause={onPause}
          onTimeUpdate={onTimeUpdate}
          onSeeking={onSeeking}
          onSeeked={onSeeked}
          onEnded={onEnded}
        />
      )}
    </div>
  );
}
